import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import {
  purchaseOrderService,
  PurchaseOrderDTO,
} from '../../services/purchaseOrderService';
import { supplierService } from '../../services/supplierService';
import paymentTypeService, { PaymentType } from '../../services/paymentTypeService';
import { useCurrency } from '../../hooks/useCurrency';
import './PurchaseOrderPage.css';
import { CirclePlus, RefreshCw, CreditCard, Eye, FileText, AlertCircle, DollarSign, Briefcase, Filter } from 'lucide-react';

import { getServerErrorMessage } from '../../utils/errorUtils';
interface Supplier {
  id: number;
  name: string;
}

type StatusFilter = 'all' | 'pending' | 'overdue';

const SupplierOutstandingPaymentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const currencyCode = useCurrency();

  // ── Data state ────────────────────────────────────────────────────────────
  const [allOrders, setAllOrders]     = useState<PurchaseOrderDTO[]>([]);
  const [suppliers, setSuppliers]     = useState<Supplier[]>([]);
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [success, setSuccess]         = useState<string | null>(null);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter]     = useState<StatusFilter>('all');
  const [supplierFilter, setSupplierFilter] = useState<number | 'all'>('all');
  const [searchPONo, setSearchPONo]         = useState('');

  // ── Payment modal state ───────────────────────────────────────────────────
  const [activePaymentTypes, setActivePaymentTypes] = useState<PaymentType[]>([]);
  const [selectedOrder, setSelectedOrder]         = useState<PurchaseOrderDTO | null>(null);
  const [showPaymentModal, setShowPaymentModal]   = useState(false);
  const [payAmount, setPayAmount]                 = useState('');
  const [payDate, setPayDate]                     = useState('');
  const [payMethod, setPayMethod]                 = useState<string>('');
  const [payReference, setPayReference]           = useState('');
  const [payNotes, setPayNotes]                   = useState('');
  const [markSettled, setMarkSettled]             = useState(false);
  const [submitting, setSubmitting]               = useState(false);
  const [payAmountError, setPayAmountError]       = useState('');

  // ── Locale helpers ────────────────────────────────────────────────────────
  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';
  const fmt = useCallback(
    (n: number) =>
      new Intl.NumberFormat(langCode, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n),
    [langCode]
  );
  const fmtDate = (d: string) => new Date(d).toLocaleDateString(langCode);

  // ── Load data ─────────────────────────────────────────────────────────────
  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const [ordersRes, suppliersRes] = await Promise.allSettled([
        purchaseOrderService.getOutstandingOrders({ pageNumber: 1, pageSize: 500 }),
        supplierService.getSuppliers({ pageSize: 1000 }),
      ]);

      if (ordersRes.status === 'fulfilled') {
        const data = ordersRes.value.data?.data;
        if (Array.isArray(data)) setAllOrders(data);
      }
      if (suppliersRes.status === 'fulfilled') {
        const data = suppliersRes.value.data?.data;
        if (Array.isArray(data)) {
          setSuppliers(data.map((s: any) => ({ id: s.id, name: s.name || `Supplier ${s.id}` })));
        }
      }
    } catch {
      setError(t('supplierOutstanding.failedToLoad') || 'Failed to load outstanding orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load active payment types once for the payment modal dropdown
  useEffect(() => {
    paymentTypeService.getActivePaymentTypes('Purchase')
      .then((types) => {
        if (Array.isArray(types) && types.length > 0) {
          setActivePaymentTypes(types);
          // Pre-select default (exclude Credit — PO payments are real money)
          const def = types.find((pt) => pt.isDefault && pt.paymentCategory !== 'Credit')
            ?? types.find((pt) => pt.paymentCategory !== 'Credit')
            ?? types[0];
          setPayMethod(def?.code ?? '');
        }
      })
      .catch(() => {});
  }, []);

  // Auto-dismiss success message
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  // ── Filtered + computed orders ────────────────────────────────────────────
  const filteredOrders = allOrders.filter(o => {
    if (statusFilter === 'pending' && o.isOverdue)  return false;
    if (statusFilter === 'overdue' && !o.isOverdue) return false;
    if (supplierFilter !== 'all' && o.supplierId !== supplierFilter) return false;
    if (searchPONo && !o.poNumber.toLowerCase().includes(searchPONo.toLowerCase())) return false;
    return true;
  });

  const totalBalance   = filteredOrders.reduce((s, o) => s + (o.balanceDue ?? 0), 0);
  const totalValue     = filteredOrders.reduce((s, o) => s + (o.totalAmount ?? 0), 0);
  const overdueCount   = filteredOrders.filter(o => o.isOverdue).length;

  // ── Open payment modal ────────────────────────────────────────────────────
  const openPayModal = (order: PurchaseOrderDTO) => {
    setSelectedOrder(order);
    setPayAmount((order.balanceDue ?? 0).toFixed(2));
    setPayDate(new Date().toISOString().split('T')[0]);
    setPayMethod(
      activePaymentTypes.find((pt) => pt.isDefault && pt.paymentCategory !== 'Credit')?.code
      ?? activePaymentTypes.find((pt) => pt.paymentCategory !== 'Credit')?.code
      ?? payMethod
    );
    setPayReference('');
    setPayNotes('');
    setMarkSettled(true);
    setPayAmountError('');
    setShowPaymentModal(true);
  };

  const closePayModal = () => {
    if (submitting) return;
    setShowPaymentModal(false);
    setSelectedOrder(null);
  };

  // ── Submit payment ────────────────────────────────────────────────────────
  const handlePaySubmit = async () => {
    if (!selectedOrder) return;

    const amount = parseFloat(payAmount);
    if (!payAmount || isNaN(amount) || amount <= 0) {
      setPayAmountError(t('validation.required') || 'Required');
      return;
    }
    if (amount > (selectedOrder.balanceDue ?? 0)) {
      setPayAmountError(t('supplierOutstanding.amountExceedsBalance') || 'Amount exceeds balance');
      return;
    }
    setPayAmountError('');

    setSubmitting(true);
    try {
      // Always record the payment transaction
      await purchaseOrderService.recordPayment(selectedOrder.id, {
        amount,
        paymentDate: payDate,
        paymentMethod: payMethod,
        referenceNumber: payReference || undefined,
        notes: payNotes || undefined,
      });

      // If full settlement requested, also settle the credit
      if (markSettled || amount >= (selectedOrder.balanceDue ?? 0)) {
        await purchaseOrderService.settleCreditOrder(selectedOrder.id, {
          settlementDate: payDate,
          remarks: payNotes || undefined,
        });
      }

      setSuccess(t('supplierOutstanding.paymentRecorded') || 'Payment recorded successfully');
      setShowPaymentModal(false);
      await loadData(true); // silent refresh
    } catch (err: any) {
      const msg = getServerErrorMessage(err) || t('supplierOutstanding.paymentFailed') || 'Failed to record payment';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── DataGrid columns ──────────────────────────────────────────────────────
  const columns: GridColumn[] = [
    {
      key: 'poNumber',
      label: t('purchaseOrder.poNumber'),
      sortable: true,
      width: '130px',
      render: (v) => <code style={{ fontWeight: 700, color: 'var(--ims-accent)' }}>{v}</code>,
    },
    {
      key: 'supplierName',
      label: t('purchaseOrder.supplier'),
      sortable: true,
      width: '180px',
      render: (v) => <strong>{v || '—'}</strong>,
    },
    {
      key: 'orderDate',
      label: t('purchaseOrder.orderDate'),
      sortable: true,
      width: '120px',
      render: (v) => fmtDate(v),
    },
    {
      key: 'expectedDeliveryDate',
      label: t('supplierOutstanding.dueDate'),
      sortable: true,
      width: '120px',
      hideTablet: true,
      render: (v, item: PurchaseOrderDTO) => (
        <span style={{ color: item.isOverdue ? 'var(--ims-red-text)' : undefined, fontWeight: item.isOverdue ? 600 : undefined }}>
          {v ? fmtDate(v) : '—'}
        </span>
      ),
    },
    {
      key: 'creditPeriodDays',
      label: t('supplierOutstanding.creditDays'),
      width: '100px',
      hideMobile: true,
      render: (v) => v ? `${v} days` : '—',
    },
    {
      key: 'totalAmount',
      label: t('purchaseOrder.totalAmount'),
      sortable: true,
      width: '130px',
      hideMobile: true,
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(v ?? 0)}</span>,
    },
    {
      key: 'amountPaid',
      label: t('supplierOutstanding.amountPaid'),
      width: '130px',
      hideMobile: true,
      render: (v) => <span style={{ color: 'var(--ims-green-text)', fontVariantNumeric: 'tabular-nums' }}>{fmt(v ?? 0)}</span>,
    },
    {
      key: 'balanceDue',
      label: t('supplierOutstanding.balanceDue'),
      sortable: true,
      width: '130px',
      render: (v, item: PurchaseOrderDTO) => (
        <strong style={{
          color: item.isOverdue ? 'var(--ims-red-text)' : 'var(--ims-amber-text)',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {fmt(v ?? 0)}
        </strong>
      ),
    },
    {
      key: 'isOverdue',
      label: t('supplierOutstanding.status'),
      width: '110px',
      render: (v: boolean) => v
        ? <span className="ims-badge ims-badge-rejected">{t('supplierOutstanding.overdue')}</span>
        : <span className="ims-badge ims-badge-pending">{t('supplierOutstanding.pending')}</span>,
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <CreditCard size={16} strokeWidth={2} />
      ),
      onClick: openPayModal,
      className: 'grid-action-btn-edit',
      title: t('supplierOutstanding.recordPayment') || 'Record Payment',
    },
    {
      icon: (
        <Eye size={16} strokeWidth={2} />
      ),
      onClick: (item: PurchaseOrderDTO) => navigate(`/purchase-orders/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="po-page">

        {/* ── Header ── */}
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('supplierOutstanding.title')}</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ims-text-muted)' }}>
              {t('supplierOutstanding.subtitle')}
            </p>
          </div>
          <div className="ims-list-actions">
            <button
              className="btn-refresh"
              onClick={() => loadData(true)}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={16} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
            <button
              className="btn-new"
              onClick={() => navigate('/purchase-orders/create')}
            >
              <CirclePlus/>
              {t('purchaseOrder.newPurchaseOrder')}
            </button>
          </div>
        </div>

        {/* ── Stat cards ── */}
        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-total">
              <FileText size={20} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{filteredOrders.length}</span>
              <span className="ims-stat-label">{t('supplierOutstanding.totalOrders')}</span>
            </div>
          </div>

          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-danger">
              <AlertCircle size={20} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value" style={{ color: overdueCount > 0 ? 'var(--ims-red-text)' : undefined }}>
                {overdueCount}
              </span>
              <span className="ims-stat-label">{t('supplierOutstanding.overdue')}</span>
            </div>
          </div>

          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-pending">
              <DollarSign size={20} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value" style={{ fontSize: 18 }}>{fmt(totalBalance)}</span>
              <span className="ims-stat-label">{t('supplierOutstanding.totalBalance')}</span>
            </div>
          </div>

          <div className="ims-stat-card">
            <div className="ims-stat-icon ims-stat-icon-info">
              <Briefcase size={20} strokeWidth={2} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value" style={{ fontSize: 18 }}>{fmt(totalValue)}</span>
              <span className="ims-stat-label">{t('supplierOutstanding.totalOrderValue')}</span>
            </div>
          </div>
        </div>

        {/* ── Alerts ── */}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            {error}
            <button className="ims-banner-close" onClick={() => setError(null)}>✕</button>
          </div>
        )}
        {success && (
          <div className="ims-alert ims-alert-success" style={{ marginBottom: 16 }}>
            {success}
            <button className="ims-banner-close" onClick={() => setSuccess(null)}>✕</button>
          </div>
        )}

        {/* ── Filters ── */}
        <div className="ims-card" style={{ marginBottom: 20 }}>
          <div className="ims-card-header">
            <span className="ims-card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Filter size={14} strokeWidth={2} />
              {t('common.filters')}
            </span>
          </div>
          <div className="ims-card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div className="ims-form-group" style={{ marginBottom: 0 }}>
              <label className="ims-form-label">{t('supplierOutstanding.status')}</label>
              <select
                className="ims-form-control"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as StatusFilter)}
              >
                <option value="all">{t('common.all')}</option>
                <option value="pending">{t('supplierOutstanding.pending')}</option>
                <option value="overdue">{t('supplierOutstanding.overdue')}</option>
              </select>
            </div>

            <div className="ims-form-group" style={{ marginBottom: 0 }}>
              <label className="ims-form-label">{t('common.supplier')}</label>
              <select
                className="ims-form-control"
                value={supplierFilter}
                onChange={e => setSupplierFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              >
                <option value="all">{t('common.all')}</option>
                {suppliers.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="ims-form-group" style={{ marginBottom: 0 }}>
              <label className="ims-form-label">{t('purchaseOrder.poNumber')}</label>
              <input
                type="text"
                className="ims-form-control"
                placeholder={t('common.search') + '...'}
                value={searchPONo}
                onChange={e => setSearchPONo(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Data Grid ── */}
        <div className="ims-grid-wrapper">
          <DataGrid
            data={filteredOrders}
            columns={columns}
            actions={actions}
            loading={loading}
            pageSize={15}
            searchPlaceholder={t('common.search')}
            emptyMessage={t('supplierOutstanding.noOrdersFound') || 'No outstanding orders'}
            rowsPerPageOptions={[10, 15, 25, 50]}
          />
        </div>
      </div>

      {/* ── Payment Modal ── */}
      {showPaymentModal && selectedOrder && (
        <div className="ims-modal-overlay" onClick={closePayModal}>
          <div className="ims-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <h2 className="ims-modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CreditCard size={18} strokeWidth={2} />
                {t('supplierOutstanding.recordPayment')}
              </h2>
              <button className="btn-close" onClick={closePayModal} disabled={submitting} />
            </div>
            <p className="ims-modal-sub">{selectedOrder.poNumber} — {selectedOrder.supplierName}</p>

            {/* Order summary */}
            <div style={{
              background: 'var(--ims-bg-row-alt)',
              border: '1px solid var(--ims-border)',
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: 20,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 12,
            }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--ims-text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>{t('purchaseOrder.totalAmount')}</div>
                <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt(selectedOrder.totalAmount ?? 0)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--ims-text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>{t('supplierOutstanding.amountPaid')}</div>
                <div style={{ fontWeight: 600, color: 'var(--ims-green-text)', fontVariantNumeric: 'tabular-nums' }}>{fmt(selectedOrder.amountPaid ?? 0)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--ims-text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 2 }}>{t('supplierOutstanding.balanceDue')}</div>
                <div style={{ fontWeight: 700, color: selectedOrder.isOverdue ? 'var(--ims-red-text)' : 'var(--ims-amber-text)', fontVariantNumeric: 'tabular-nums', fontSize: 16 }}>
                  {fmt(selectedOrder.balanceDue ?? 0)}
                </div>
              </div>
            </div>

            {/* Payment form */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="ims-form-group" style={{ marginBottom: 0 }}>
                <label className="ims-form-label">
                  {t('supplierOutstanding.paymentAmount')} <span className="required">*</span>
                </label>
                <input
                  type="number"
                  className={`ims-form-control${payAmountError ? ' is-invalid' : ''}`}
                  value={payAmount}
                  onChange={e => {
                    setPayAmount(e.target.value);
                    setPayAmountError('');
                    const v = parseFloat(e.target.value);
                    setMarkSettled(!isNaN(v) && v >= (selectedOrder.balanceDue ?? 0));
                  }}
                  step="0.01"
                  min="0.01"
                  max={selectedOrder.balanceDue ?? undefined}
                  disabled={submitting}
                />
                {payAmountError && <small className="ims-field-error">{payAmountError}</small>}
              </div>

              <div className="ims-form-group" style={{ marginBottom: 0 }}>
                <label className="ims-form-label">
                  {t('supplierOutstanding.paymentDate')} <span className="required">*</span>
                </label>
                <input
                  type="date"
                  className="ims-form-control"
                  value={payDate}
                  onChange={e => setPayDate(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="ims-form-group" style={{ marginBottom: 0 }}>
                <label className="ims-form-label">{t('supplierOutstanding.paymentMethod')} <span className="required">*</span></label>
                <select
                  className="ims-form-control"
                  value={payMethod}
                  onChange={e => setPayMethod(e.target.value)}
                  disabled={submitting}
                >
                  {activePaymentTypes.map(pt => (
                    <option key={pt.id} value={pt.code}>{pt.name}</option>
                  ))}
                </select>
              </div>

              <div className="ims-form-group" style={{ marginBottom: 0 }}>
                <label className="ims-form-label">{t('supplierOutstanding.referenceNumber')}</label>
                <input
                  type="text"
                  className="ims-form-control"
                  value={payReference}
                  onChange={e => setPayReference(e.target.value)}
                  placeholder={t('common.optional') || 'Optional'}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="ims-form-group" style={{ marginTop: 14 }}>
              <label className="ims-form-label">{t('supplierOutstanding.remarks')}</label>
              <textarea
                className="ims-form-control ims-textarea"
                rows={2}
                value={payNotes}
                onChange={e => setPayNotes(e.target.value)}
                placeholder={t('common.optional') || 'Optional'}
                disabled={submitting}
              />
            </div>

            {/* Settle checkbox */}
            <label className="form-check" style={{ marginTop: 4, marginBottom: 20 }}>
              <input
                type="checkbox"
                className="form-check-input"
                checked={markSettled}
                onChange={e => setMarkSettled(e.target.checked)}
                disabled={submitting}
              />
              <span className="form-check-label" style={{ fontSize: 13 }}>
                {t('supplierOutstanding.markFullySettled') || 'Mark order as fully settled'}
              </span>
            </label>

            {/* Modal footer */}
            <div className="ims-modal-footer">
              <button className="btn-cancel" onClick={closePayModal} disabled={submitting}>
                {t('common.cancel')}
              </button>
              <button
                className="btn-save"
                onClick={handlePaySubmit}
                disabled={submitting || !payAmount || !payDate}
              >
                {submitting
                  ? <><span className="btn-spinner" /> {t('common.processing')}...</>
                  : t('supplierOutstanding.recordPayment')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SupplierOutstandingPaymentsPage;
